import { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

export const EnquiryForm = ({ variant = 'default', coursePreselect = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: coursePreselect || '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Enter a valid 10-digit phone number';
    }
    if (!formData.course) newErrors.course = 'Please select a course';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Mock submission - stores in localStorage
    const enquiries = JSON.parse(localStorage.getItem('scaleup_enquiries') || '[]');
    enquiries.push({ ...formData, timestamp: new Date().toISOString(), id: Date.now() });
    localStorage.setItem('scaleup_enquiries', JSON.stringify(enquiries));

    setSubmitted(true);
    toast.success('Enquiry submitted successfully! We will contact you soon.');
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', phone: '', course: '', message: '' });
    }, 3000);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 rounded-full bg-[rgba(243,112,33,0.1)] flex items-center justify-center mb-4">
          <CheckCircle size={32} className="text-[#F37021]" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">Thank You!</h3>
        <p className="text-[rgb(161,161,170)] text-sm">Your enquiry has been submitted. We'll get back to you within 24 hours.</p>
      </div>
    );
  }

  const isCompact = variant === 'compact';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className={isCompact ? 'space-y-4' : 'grid grid-cols-1 md:grid-cols-2 gap-5'}>
        <div className="space-y-2">
          <Label className="text-[rgb(218,218,218)] text-sm font-medium">Full Name *</Label>
          <Input
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Enter your full name"
            className="bg-[rgb(26,28,30)] border-2 border-[rgb(63,63,63)] rounded-xl h-12 text-white placeholder:text-[rgb(161,161,170)] focus:border-[#F37021] focus:ring-2 focus:ring-[rgba(243,112,33,0.1)] transition-all duration-200"
          />
          {errors.name && <p className="text-red-400 text-xs">{errors.name}</p>}
        </div>

        <div className="space-y-2">
          <Label className="text-[rgb(218,218,218)] text-sm font-medium">Email *</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="your.email@example.com"
            className="bg-[rgb(26,28,30)] border-2 border-[rgb(63,63,63)] rounded-xl h-12 text-white placeholder:text-[rgb(161,161,170)] focus:border-[#F37021] focus:ring-2 focus:ring-[rgba(243,112,33,0.1)] transition-all duration-200"
          />
          {errors.email && <p className="text-red-400 text-xs">{errors.email}</p>}
        </div>

        <div className="space-y-2">
          <Label className="text-[rgb(218,218,218)] text-sm font-medium">Phone Number *</Label>
          <Input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="10-digit mobile number"
            className="bg-[rgb(26,28,30)] border-2 border-[rgb(63,63,63)] rounded-xl h-12 text-white placeholder:text-[rgb(161,161,170)] focus:border-[#F37021] focus:ring-2 focus:ring-[rgba(243,112,33,0.1)] transition-all duration-200"
          />
          {errors.phone && <p className="text-red-400 text-xs">{errors.phone}</p>}
        </div>

        <div className="space-y-2">
          <Label className="text-[rgb(218,218,218)] text-sm font-medium">Course Interest *</Label>
          <Select value={formData.course} onValueChange={(val) => handleChange('course', val)}>
            <SelectTrigger className="bg-[rgb(26,28,30)] border-2 border-[rgb(63,63,63)] rounded-xl h-12 text-white focus:border-[#F37021] focus:ring-2 focus:ring-[rgba(243,112,33,0.1)] transition-all duration-200">
              <SelectValue placeholder="Select a course" />
            </SelectTrigger>
            <SelectContent className="bg-[rgb(26,28,30)] border border-[rgb(63,63,63)]">
              <SelectItem value="clat">CLAT Preparation</SelectItem>
              <SelectItem value="cuet">CUET Coaching</SelectItem>
              <SelectItem value="ielts">IELTS Training</SelectItem>
              <SelectItem value="spoken-english">Spoken English</SelectItem>
            </SelectContent>
          </Select>
          {errors.course && <p className="text-red-400 text-xs">{errors.course}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-[rgb(218,218,218)] text-sm font-medium">Message (Optional)</Label>
        <Textarea
          value={formData.message}
          onChange={(e) => handleChange('message', e.target.value)}
          placeholder="Tell us about your requirements..."
          rows={4}
          className="bg-[rgb(26,28,30)] border-2 border-[rgb(63,63,63)] rounded-xl text-white placeholder:text-[rgb(161,161,170)] focus:border-[#F37021] focus:ring-2 focus:ring-[rgba(243,112,33,0.1)] transition-all duration-200 resize-none"
        />
      </div>

      <Button
        type="submit"
        className="w-full bg-[#F37021] hover:bg-[#DC5A14] text-white font-semibold rounded-xl h-13 text-base border-0 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(243,112,33,0.35)]"
      >
        Submit Enquiry <Send size={18} className="ml-2" />
      </Button>
    </form>
  );
};
